import Avatar from "components/avatar";
import Badge from "components/badge/Badge";
import { PageHeader } from "components/page";
import TagBadge from "components/tags/TagBadge";
import { roleTone, statusTone } from "lib/statusTone";

export const personName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ");

/**
 * Role, status and tag badges for a person. Roles are labels, so they render neutral.
 *
 * @param {object} props
 * @param {object} props.user
 * @param {boolean} [props.showTags=true]
 * @param {string} [props.className]
 */
export const PersonBadges = ({ user, showTags = true, className = "" }) => {
  const tags = showTags ? user?.tags || [] : [];
  if (!user?.role && !user?.status && tags.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {user?.role && (
        <Badge tone={roleTone(user.role)} className="capitalize">
          {user.role}
        </Badge>
      )}
      {user?.status && (
        <Badge tone={statusTone(user.status)} dot className="capitalize">
          {user.status}
        </Badge>
      )}
      {tags.map((tag) => (
        <TagBadge key={tag.id} tag={tag} />
      ))}
    </div>
  );
};

/**
 * Person detail header: PageHeader with the person's avatar, name (the page's h1),
 * contact line, badges and actions.
 *
 * @param {object} props
 * @param {object} props.user
 * @param {string} [props.backTo] Path for the back button.
 * @param {string} [props.backLabel="Back"]
 * @param {React.ReactNode} [props.actions]
 */
const PersonHeader = ({ user, backTo, backLabel = "Back", actions }) => {
  const contact = [user?.email, user?.phone].filter(Boolean).join(" · ");
  return (
    <PageHeader
      title={personName(user)}
      leading={<Avatar user={user} size="lg" noBorder decorative />}
      meta={<PersonBadges user={user} />}
      description={contact || undefined}
      backTo={backTo || undefined}
      backLabel={backLabel}
      actions={actions}
    />
  );
};

export default PersonHeader;
